/**
 * EmailDeliverabilityPage — Admin DNS-health check voor email-domein.
 *
 * Toont SPF / DKIM / DMARC / MX status met aanbevolen DNS-records die de admin
 * uit de bestaande DNS-instellingen van Mijndomein worden gelezen. Geen DNS-wijzigingen.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  Loader2, RefreshCw, ShieldCheck, ShieldAlert, ShieldX,
  Copy, Mail, Server, Globe,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_STYLES = {
  info: { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.3)', color: '#10b981', icon: ShieldCheck, label: 'OK' },
  warning: { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.4)', color: '#f59e0b', icon: ShieldAlert, label: 'Let op' },
  critical: { bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.4)', color: '#ef4444', icon: ShieldX, label: 'Kritiek' },
};

export default function EmailDeliverabilityPage() {
  const { getToken } = useAuth();
  const token = getToken();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [domain, setDomain] = useState('imperiaai.nl');

  const run = async (dom = domain) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/email/deliverability`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { domain: dom },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'DNS lookup mislukt');
    }
    setLoading(false);
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, [token]);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Gekopieerd naar klembord');
  };

  return (
    <div className="p-0 sm:p-6" style={{ color: 'white', maxWidth: 1080, minWidth: 0 }} data-testid="email-deliverability-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 28, fontWeight: 800, margin: 0 }}>Email deliverability</h1>
          <p style={{ color: '#94a3b8', marginTop: 4, fontSize: 14 }}>SPF, DKIM en DMARC status. Verbeter de aanwijzingen om mails niet meer in spam te laten belanden.</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxWidth: '100%' }}>
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            data-testid="dns-domain-input"
            placeholder="domein.nl"
            style={{ background: '#0f172a', color: 'white', border: '1px solid #1e293b', padding: '8px 12px', borderRadius: 8, fontSize: 13, flex: '1 1 180px', minWidth: 0, maxWidth: '100%' }}
          />
          <button onClick={() => run(domain)} data-testid="dns-refresh" style={{ background: '#D4A017', color: '#0f172a', fontWeight: 700, padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Controleer
          </button>
        </div>
      </div>

      {loading && <div style={{ padding: 40, color: '#94a3b8' }}><Loader2 size={20} className="animate-spin" /> Laden…</div>}

      {!loading && data && (
        <>
          {/* Score */}
          <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 14, padding: 20, marginBottom: 20, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 24 }}>
            <div style={{ width: 130, height: 130, borderRadius: '50%', background: `conic-gradient(${data.score >= 90 ? '#10b981' : data.score >= 70 ? '#D4A017' : data.score >= 40 ? '#f59e0b' : '#ef4444'} ${data.score * 3.6}deg, #1e293b 0)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <div style={{ width: 110, height: 110, background: '#0f172a', borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 32, fontWeight: 800, color: 'white' }} data-testid="dns-score">{data.score}</span>
                <span style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>van 100</span>
              </div>
            </div>
            <div style={{ flex: '1 1 180px', minWidth: 0, overflowWrap: 'anywhere' }}>
              <p style={{ color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0, fontWeight: 700 }}>Domein</p>
              <p style={{ fontSize: 22, fontWeight: 800, margin: '4px 0', fontFamily: 'monospace' }} data-testid="dns-domain">{data.domain}</p>
              <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>
                Status: <strong style={{ color: data.overall_status === 'excellent' ? '#10b981' : data.overall_status === 'good' ? '#D4A017' : data.overall_status === 'fair' ? '#f59e0b' : '#ef4444', textTransform: 'capitalize' }}>{data.overall_status}</strong>
              </p>
              <p style={{ color: '#64748b', fontSize: 12, margin: '4px 0 0' }}>Gemeten: {(data.checked_at || '').slice(0, 19).replace('T', ' ')} UTC</p>
            </div>
          </div>

          {/* Records grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: 16 }}>
            <RecordCard title="SPF" icon={Mail} data={data.spf} copy={copy} testId="dns-spf" />
            <RecordCard title="DKIM" icon={ShieldCheck} data={data.dkim} copy={copy} testId="dns-dkim" />
            <RecordCard title="DMARC" icon={Server} data={data.dmarc} copy={copy} testId="dns-dmarc" />
            <RecordCard title="MX" icon={Globe} data={data.mx} copy={copy} testId="dns-mx" />
          </div>

          {/* Help block */}
          <div style={{ background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.3)', borderRadius: 12, padding: 18, marginTop: 20, fontSize: 13, color: '#fde68a' }}>
            <p data-testid="dns-read-only-notice" style={{ margin: 0, fontWeight: 700, color: '#D4A017' }}>Mijndomein · alleen-lezencontrole</p>
            <p data-testid="dns-records-preserved" style={{ margin: '8px 0 0', lineHeight: 1.8 }}>Je bestaande DNS-records blijven ongewijzigd. Deze pagina leest uitsluitend de instellingen van {data.domain}.</p>
          </div>

          {/* SMTP config */}
          <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 12, padding: 18, marginTop: 16, fontSize: 13, color: '#cbd5e1' }}>
            <p style={{ margin: 0, fontWeight: 700, color: 'white' }}>Huidige SMTP configuratie</p>
            <table style={{ marginTop: 8, width: '100%', tableLayout: 'fixed', overflowWrap: 'anywhere', fontSize: 13 }}>
              <tbody>
                <tr><td style={{ color: '#94a3b8', padding: '3px 12px 3px 0' }}>SMTP host:</td><td data-testid="dns-smtp-host" style={{ fontFamily: 'monospace' }}>{data.smtp_config?.smtp_host}</td></tr>
                <tr><td style={{ color: '#94a3b8', padding: '3px 12px 3px 0' }}>SMTP from:</td><td data-testid="dns-smtp-from" style={{ fontFamily: 'monospace' }}>{data.smtp_config?.smtp_from}</td></tr>
              </tbody>
            </table>
          </div>

          {/* Auto-check history */}
          <DnsHistory token={token} domain={data.domain} />
        </>
      )}
    </div>
  );
}

function DnsHistory({ token, domain }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/admin/email/deliverability/history`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { domain, limit: 30 },
        });
        setHistory(r.data.history || []);
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
  }, [token, domain]);

  if (loading) return null;
  if (!history.length) return (
    <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 12, padding: 18, marginTop: 16, fontSize: 12, color: '#94a3b8' }} data-testid="dns-history-empty">
      Nog geen historische checks. De DNS Auto-Check draait dagelijks om 08:00 UTC en verschijnt hier automatisch.
    </div>
  );

  return (
    <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 12, padding: 18, marginTop: 16 }} data-testid="dns-history">
      <p style={{ margin: 0, fontWeight: 700, color: 'white', fontSize: 14 }}>Historie — laatste {history.length} checks</p>
      <p style={{ margin: '4px 0 12px', color: '#64748b', fontSize: 12 }}>Automatisch dagelijks 08:00 UTC · admins krijgen mail bij regressie</p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', tableLayout: 'fixed', overflowWrap: 'anywhere', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#94a3b8', textAlign: 'left' }}>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #1e293b' }}>Datum (UTC)</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #1e293b' }}>Score</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #1e293b' }}>Status</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #1e293b' }}>SPF</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #1e293b' }}>DKIM</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #1e293b' }}>DMARC</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #1e293b' }}>MX</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h, i) => (
              <tr key={i} style={{ color: '#cbd5e1' }}>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11 }}>{(h.checked_at || '').slice(0, 16).replace('T', ' ')}</td>
                <td style={{ padding: '6px 8px', fontWeight: 700, color: h.score >= 90 ? '#10b981' : h.score >= 70 ? '#D4A017' : h.score >= 40 ? '#f59e0b' : '#ef4444' }}>{h.score}</td>
                <td style={{ padding: '6px 8px', textTransform: 'capitalize' }}>{h.overall_status}</td>
                <td style={{ padding: '6px 8px' }}>{h.spf?.found ? '✓' : '—'}</td>
                <td style={{ padding: '6px 8px' }}>{h.dkim?.found ? '✓' : '—'}</td>
                <td style={{ padding: '6px 8px' }}>{h.dmarc?.found ? '✓' : '—'}</td>
                <td style={{ padding: '6px 8px' }}>{h.mx?.found ? '✓' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecordCard({ title, icon: Icon, data, copy, testId }) {
  if (!data) return null;
  const sev = SEVERITY_STYLES[data.severity] || SEVERITY_STYLES.warning;
  const SevIcon = sev.icon;
  return (
    <div data-testid={testId} style={{ background: sev.bg, border: `1px solid ${sev.border}`, borderRadius: 12, padding: 16, minWidth: 0, overflowWrap: 'anywhere' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Icon size={16} color={sev.color} />
        <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0, color: 'white' }}>{title}</h3>
        <span style={{ background: sev.color, color: '#0f172a', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 800, marginLeft: 'auto', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {sev.label}
        </span>
      </div>
      <p style={{ color: '#cbd5e1', fontSize: 13, margin: '0 0 10px', lineHeight: 1.5 }}>
        <SevIcon size={14} style={{ display: 'inline', marginRight: 4, color: sev.color }} />
        {data.message}
      </p>

      {data.record && (
        <div style={{ background: '#0f172a', borderRadius: 6, padding: 10, fontFamily: 'monospace', fontSize: 11, color: '#a7f3d0', wordBreak: 'break-all', position: 'relative', marginBottom: 6 }}>
          {data.record}
          <button data-testid={`${testId}-copy-record`} aria-label={`${title} record kopiëren`} onClick={() => copy(data.record)} style={{ position: 'absolute', right: 6, top: 6, background: 'rgba(255,255,255,0.05)', border: 'none', color: '#64748b', cursor: 'pointer', padding: 4, borderRadius: 4 }}>
            <Copy size={11} />
          </button>
        </div>
      )}

      {data.records && Array.isArray(data.records) && data.records.length > 0 && (
        <div style={{ background: '#0f172a', borderRadius: 6, padding: 10, fontSize: 11, color: '#cbd5e1', marginBottom: 6 }}>
          {data.records.map((r, i) => (
            <div data-testid={`${testId}-record-${i}`} key={i} style={{ fontFamily: 'monospace', marginBottom: 4, overflowWrap: 'anywhere' }}>
              {typeof r === 'string' ? r : `${r.preference} ${r.exchange}`}
            </div>
          ))}
        </div>
      )}

      {data.selectors && (
        <div style={{ background: '#0f172a', borderRadius: 6, padding: 10, fontSize: 11, color: '#a7f3d0', marginBottom: 6 }}>
          {Object.entries(data.selectors).map(([sel, val]) => (
            <div key={sel} style={{ marginBottom: 4 }}>
              <strong style={{ color: '#D4A017' }}>{sel}</strong>: <span data-testid={`${testId}-selector-${sel}`} style={{ fontFamily: 'monospace', overflowWrap: 'anywhere', wordBreak: 'break-all' }}>{val}</span>
            </div>
          ))}
        </div>
      )}

      {data.recommended && (
        <div style={{ marginTop: 8 }}>
          <p style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, margin: '0 0 4px' }}>Aanbevolen TXT record</p>
          <div style={{ background: '#0f172a', borderRadius: 6, padding: 10, fontFamily: 'monospace', fontSize: 11, color: '#fde68a', wordBreak: 'break-all', position: 'relative' }}>
            {data.recommended}
            <button data-testid={`${testId}-copy-recommended`} aria-label={`${title} aanbevolen record kopiëren`} onClick={() => copy(data.recommended)} style={{ position: 'absolute', right: 6, top: 6, background: 'rgba(255,255,255,0.05)', border: 'none', color: '#64748b', cursor: 'pointer', padding: 4, borderRadius: 4 }}>
              <Copy size={11} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
